import { toast } from 'react-toastify'
import { toBufferLE } from 'bigint-buffer'
import BigNumber from 'bignumber.js'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { BN } from '@coral-xyz/anchor'
import { PublicKey } from '@solana/web3.js'

import ENV_PARAMS from '@/constants/envParams'
import { Decimal } from '@/constants/trade'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

//Bignumber正常数字格式化参数
export const fmtNatural = {
  groupSize: 0,
  groupSeparator: '',
  decimalSeparator: '.'
}

//Bignumber整数千分位展示
export const fmtThousand = {
  groupSize: 3,
  groupSeparator: ',',
  decimalSeparator: '.'
}

/**
 * 小单位 -> 大单位
 * @param number 数字/数字字符串
 * @param decimals 精度，默认18
 * @param fmt 转出的字符串格式, 默认自然数字格式
 * @returns {*}
 */
export function min2Max(
  number: BigNumber.Value,
  decimals = 18,
  fmt?: any
): string {
  //console.error(number, typeof number)
  // console.error(decimals, typeof decimals)
  // return new BigNumber(EtherUtils.formatUnits(number, decimals)).toFormat(fmt ? fmt : this.fmtNatural);
  const result = new BigNumber(number).dividedBy(
    new BigNumber(10).pow(decimals)
  )
  return result.toFormat(fmt ? fmt : fmtNatural)
}

/**
 * 大单位 -> 小单位
 * @param number 数字/数字字符串
 * @param decimals 精度，默认18
 * @param fmt 转出的字符串格式, 默认自然数字格式
 * @returns {*}
 */
export function max2Min(
  number: BigNumber.Value,
  decimals = 18,
  fmt?: any
): string {
  const result = new BigNumber(number).multipliedBy(
    new BigNumber(10).pow(decimals)
  )
  return result.toFormat(fmt ? fmt : fmtNatural)
}

/************************** 大数的【 加、减、乘、除 】说明 ************************/
// 本工具类中都有对应方法，此处再说明下
// 格式化成字符串：
// BigNumber.toFormat(fmtNatural) 常规格式
// BigNumber.toFormat(fmtThousand) 千分位格式
// 小数位的处理
// BigNumber.dp(保留小数位数, 保留方式)
/************************************************************************** /

 /**
 * 两数比较 a 与 b 比较
 * @param a  string|number|BigNumber
 * @param symbol 比较符号5选1，有： < 、 <= 、 = 、 > 、 >=
 * @param b  string|number|BigNumber
 * @returns {boolean}
 */
export function bigNumberCompare(
  a: BigNumber.Value,
  symbol: string,
  b: BigNumber.Value
) {
  const aa = new BigNumber(a)
  const bb = new BigNumber(b)
  let flag = false
  switch (symbol) {
    case '<':
      flag = aa.lt(bb)
      break
    case '<=':
      flag = aa.lte(bb)
      break
    case '=':
      flag = aa.eq(bb)
      break
    case '>':
      flag = aa.gt(bb)
      break
    case '>=':
      flag = aa.gte(bb)
      break
  }
  return flag
}

/**
 * 两数相乘 a * b
 * @param a string|number|BigNumber
 * @param b string|number|BigNumber
 * @param dp 小数位数(精度数),默认2位小数
 * @param md 小数截取方式,默认1直接截断，常见方式说明：
 * ROUND_UP：0表示，进位制：不管保留精度数字后面是大是小(0除外)都会进1，当dp=2,则3.321为3.33
 * ROUND_DOWN：1表示，保留精度数，后面所有直接去除，当dp=2,则3.321为3.32
 * ROUND_HALF_UP：4表示，保留精度的后一位>=5进行四舍五入，当dp=2,则3.325为3.33
 * ROUND_HALF_DOWN：5表示，保留精度的后一位<=5，进行舍弃，否则进一位。当dp=2,则3.325为3.32
 * @returns {BigNumber}
 */
export function bigNumberMultiplyBy(
  a: BigNumber.Value,
  b: BigNumber.Value,
  dp = 2,
  md: BigNumber.RoundingMode = BigNumber.ROUND_DOWN
) {
  return new BigNumber(a).multipliedBy(new BigNumber(b)).dp(dp, md)
}

/**
 * 两数相除 a / b
 * @param a string|number|BigNumber
 * @param b string|number|BigNumber
 * @param dp 小数位数(精度数),默认2位小数
 * @param md 小数截取方式,默认1直接截断，常见方式说明：
 * ROUND_UP：0表示，进位制：不管保留精度数字后面是大是小(0除外)都会进1，当dp=2,则3.321为3.33
 * ROUND_DOWN：1表示，保留精度数，后面所有直接去除，当dp=2,则3.321为3.32
 * ROUND_HALF_UP：4表示，保留精度的后一位>=5进行四舍五入，当dp=2,则3.325为3.33
 * ROUND_HALF_DOWN：5表示，保留精度的后一位<=5，进行舍弃，否则进一位。当dp=2,则3.325为3.32
 * @returns {BigNumber}
 */
export function bigNumberDividedBy(
  a: BigNumber.Value,
  b: BigNumber.Value,
  dp = 2,
  md: BigNumber.RoundingMode = BigNumber.ROUND_DOWN
) {
  return new BigNumber(a).dividedBy(new BigNumber(b)).dp(dp, md)
}

/**
 * 两数相加 a + b
 * @param a string|number|BigNumber
 * @param b string|number|BigNumber
 * @returns {BigNumber}
 */
export function bigNumberPlus(a: BigNumber.Value, b: BigNumber.Value) {
  return new BigNumber(a).plus(new BigNumber(b))
}

/**
 * 两数相减 a - b
 * @param a string|number|BigNumber
 * @param b string|number|BigNumber
 * @returns {BigNumber}
 */
export function bigNumberMinus(a: BigNumber.Value, b: BigNumber.Value) {
  return new BigNumber(a).minus(new BigNumber(b))
}

export function getInt64Bytes(x: bigint) {
  return new BN(toBufferLE(x, 8))
}

export const findPersonPda = (id: number): PublicKey => {
  const tgData = new BN(getInt64Bytes(BigInt(id)))
  const buffer = Buffer.from(tgData.toString(16), 'hex')
  const [tg1Pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('rebate-person'), buffer],
    new PublicKey(ENV_PARAMS.rebateAddress)
  )
  return tg1Pda
}
export const findPersonStorePda = (personPda: PublicKey): PublicKey => {
  const [storeMoneyPda] = PublicKey.findProgramAddressSync(
    [personPda.toBuffer(), Buffer.from('store-money')],
    new PublicKey(ENV_PARAMS.rebateAddress)
  )
  return storeMoneyPda
}

export const findPersonStoreTokenPda = (personPda: PublicKey): PublicKey => {
  const [tg1StoreToken] = PublicKey.findProgramAddressSync(
    [personPda.toBuffer(), Buffer.from('store-token')],
    new PublicKey(ENV_PARAMS.rebateAddress)
  )
  return tg1StoreToken
}

export function getObjectKeyFromValue(value: any, object: any) {
  return Object.keys(object).find((key) => object[key] === value)
}

export function copy(text: string) {
  navigator.clipboard.writeText(text)
  toast.success('copied')
}

export function formatCompact(num: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact' }).format(num)
}

export function formatSolAmount(amount: BigNumber.Value): string {
  return new BigNumber(amount).toFixed(Decimal.SOL)
}

export function getImage(img: File | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    let imgSrc: string
    let blobUrl: string
    const objType = Object.prototype.toString.call(img)
    if (objType === '[object String]') {
      imgSrc = img as string
    } else if (objType === '[object File]') {
      blobUrl = URL.createObjectURL(img as File)
      imgSrc = blobUrl
    } else {
      reject('Unknow image type')
      return
    }
    image.onload = () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
      resolve(image)
    }
    image.onerror = reject
    image.src = imgSrc
  })
}

export async function wait(timeout: number = 3000) {
  return new Promise((resolve) => setTimeout(resolve, timeout))
}

export const deleteUrlParams = (keys: string[]) => {
  const url = new URL(window.location.href)
  keys.forEach((key) => {
    url.searchParams.delete(key)
  })
  window.history.replaceState({}, '', url)
}

export function getUrlParams(key: string) {
  const { searchParams } = new URL(window.location.href)
  return searchParams.get(key)
}

export function getDeviceType() {
  if (typeof window === 'undefined')
    return { pc: true, ios: false, android: false, mobile: false }
  const userAgent = navigator.userAgent || navigator.vendor

  const isAndroid = /Android/i.test(userAgent)
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent)
  const isMobile =
    /Mobile|Tablet|iPad|iPhone|iPod|Android/i.test(userAgent) ||
    window.innerWidth < 640

  return {
    pc: !isAndroid && !isIOS && !isMobile,
    ios: isIOS,
    android: isAndroid,
    mobile: isMobile
  }
}
